import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import InputItem from './InputItem';
import SelectorItem from './SelectorItem';
import ImportButton from './ImportButton';
import ExportButton from './ExportButton';
import AddDataButton from './AddDataButton';

/** SearchBar：展示查询条件及相关操作按钮 */
export default function SearchBar({
  options = [],
  onSearch = () => {},
  onReset = () => {},
  onImport,
  importAPI,
  importEventData = true,
  exportAPI,
  exportDisabled = false,
  onCreate,
  addType,
  searchOnShow = true,
  demoDownloadUrl = '',
  queryExtra,
}) {
  const initValues = useMemo(() => {
    return options.reduce((acc, cur) => {
      if (cur.type === 'date') return { ...acc, [cur.value]: null };
      return { ...acc, [cur.value]: '' };
    }, {});
  }, [options]);

  const [values, setValues] = useState(initValues);

  useEffect(() => {
    if (searchOnShow) onSearch();
  }, [onSearch, searchOnShow]);

  const onClickSearch = useCallback(() => {
    const queryString = Object.entries(values)
      .filter(([key, value]) => {
        // 过滤掉空值、null、undefined
        if (value === null || value === undefined || value === '') {
          return false;
        }
        // 如果是数组（日期范围），检查是否有值
        if (Array.isArray(value)) {
          return (
            value.length > 0 &&
            value.every((v) => v !== null && v !== undefined)
          );
        }
        return true;
      })
      .map(([key, value]) => {
        // 处理数组类型（如日期范围）
        if (Array.isArray(value)) {
          return `${key}=${value.join(',')}`;
        }
        return `${key}=${encodeURIComponent(String(value).trim())}`;
      })
      .join('&');
    onSearch(queryString);
  }, [onSearch, values]);

  const handleChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleReset = () => {
    setValues(initValues);
    onReset();
  };

  const actionButtons = (
    <>
      <button
        onClick={onClickSearch}
        className="h-9 flex items-center justify-center gap-2 px-4 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 shadow-sm transition-colors cursor-pointer"
      >
        <SearchOutlined />
        <span>查询</span>
      </button>
      <button
        onClick={handleReset}
        className="h-9 flex items-center justify-center gap-2 px-4 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
      >
        <ReloadOutlined />
        <span>刷新</span>
      </button>
      {onImport && (
        <ImportButton
          api={importAPI}
          onSuccess={onImport}
          importEventData={importEventData}
          demoDownloadUrl={demoDownloadUrl}
        />
      )}
      {exportAPI && (
        <ExportButton api={exportAPI} disabled={exportDisabled} />
      )}
      {onCreate && (
        <button
          onClick={onCreate}
          className="h-9 flex items-center justify-center gap-2 px-4 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 shadow-sm transition-colors cursor-pointer"
        >
          <PlusOutlined />
          <span>新增</span>
        </button>
      )}
      {addType && <AddDataButton type={addType} onSuccess={onSearch} />}
    </>
  );

  return (
    <div
      className={`w-full space-y-4 flex-col xl:space-y-0 xl:flex xl:flex-row xl:items-end ${
        queryExtra ? 'xl:flex-wrap xl:gap-y-4' : ''
      }`}
    >
      <div
        className={`${queryExtra ? 'xl:flex-none' : 'flex-1'} flex flex-wrap gap-4`}
      >
        {options.map((item) => (
          <div key={item.value} className="">
            {(!item.type || item.type === 'text') && (
              <InputItem
                label={item.label}
                value={values[item.value] ?? ''}
                onChange={(v) => handleChange(item.value, v)}
              />
            )}
            {item.type === 'select' && (
              <SelectorItem
                label={item.label}
                type="default"
                options={item.options || []}
                value={values[item.value]}
                onChange={(v) => handleChange(item.value, v)}
              />
            )}

            {item.type === 'time' && (
              <SelectorItem
                label={item.label}
                type="time"
                value={values[item.value]}
                onChange={(v) => handleChange(item.value, v)}
              />
            )}
          </div>
        ))}
      </div>

      <div
        className={`flex items-end justify-end gap-2 ${
          queryExtra ? 'flex-wrap-reverse xl:flex-1' : ''
        }`}
      >
        {queryExtra}
        {queryExtra ? (
          <div className="flex shrink-0 items-end justify-end gap-2">
            {actionButtons}
          </div>
        ) : (
          actionButtons
        )}
      </div>
    </div>
  );
}
